import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import { MouseEventHandler, ReactNode } from 'react'

type Props = {
  setUpload: () => void
  createNewFolder: () => void
  showDownloadOptions: () => void
  showDeleteOptions: () => void
  showIncludeInExcel: () => void
  upload: boolean
  uploadOnProgress: boolean
  numberOfFiles: number
  allFilesProcessed: boolean
  setSelectMultipleFiles: (select: boolean) => void
}

type CustomMenuItemProps = {
  disabled: boolean
  OnClick: MouseEventHandler<HTMLButtonElement> | undefined
  icon: JSX.Element
  text: ReactNode
}

const CustomMenuItem = ({
  disabled,
  icon,
  OnClick,
  text,
}: CustomMenuItemProps) => {
  return (
    <MenuItem disabled={disabled}>
      <button
        className={`flex gap-3 items-center w-full p-3 font-medium rounded-lg transition-all duration-200 ${
          disabled
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-800 hover:bg-green-50 hover:text-green-700'
        }`}
        onClick={OnClick}
      >
        <div className="flex-shrink-0">{icon}</div>
        <div className="flex-grow text-left">{text}</div>
      </button>
    </MenuItem>
  )
}

const MobileHeader = ({
  createNewFolder,
  setUpload,
  showDeleteOptions,
  showDownloadOptions,
  showIncludeInExcel,
  upload,
  uploadOnProgress,
  numberOfFiles,
  allFilesProcessed,
  setSelectMultipleFiles,
}: Props) => {
  return (
    <div className="flex justify-end items-center min-w-[100px] sm:hidden">
      <Menu>
        <MenuButton className="mr-4 p-2 rounded-xl bg-white border border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-600 transition-all duration-200 ease-in-out hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-20">
          <ExpandCircleDownOutlinedIcon sx={{ fontSize: 20 }} />
        </MenuButton>
        <MenuItems
          anchor="bottom end"
          className="z-50 mt-2 w-56 bg-white rounded-xl shadow-lg ring-1 ring-black/5 border border-gray-100 focus:outline-none overflow-hidden"
        >
          <div className="p-2 space-y-1">
            <CustomMenuItem
              OnClick={setUpload}
              disabled={upload || uploadOnProgress}
              icon={<CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />}
              text={'Upload files'}
            />

            <CustomMenuItem
              OnClick={() => setSelectMultipleFiles(true)}
              disabled={upload || uploadOnProgress}
              icon={<CheckBoxOutlinedIcon sx={{ fontSize: 20 }} />}
              text={'Select files'}
            />

            <CustomMenuItem
              OnClick={createNewFolder}
              disabled={upload || uploadOnProgress}
              icon={<CreateNewFolderOutlinedIcon sx={{ fontSize: 20 }} />}
              text={'Create folder'}
            />

            <CustomMenuItem
              OnClick={showIncludeInExcel}
              disabled={
                upload ||
                uploadOnProgress ||
                !allFilesProcessed ||
                !numberOfFiles
              }
              icon={<CloudDownloadOutlinedIcon sx={{ fontSize: 20 }} />}
              text={'Include in Excel'}
            />

            <CustomMenuItem
              OnClick={showDownloadOptions}
              disabled={upload || !allFilesProcessed || !numberOfFiles}
              icon={<FileUploadOutlinedIcon sx={{ fontSize: 20 }} />}
              text={'Export options'}
            />

            <CustomMenuItem
              OnClick={showDeleteOptions}
              disabled={upload || !allFilesProcessed || !numberOfFiles}
              icon={<DeleteOutlinedIcon sx={{ fontSize: 20 }} />}
              text={'Delete options'}
            />
          </div>
        </MenuItems>
      </Menu>
    </div>
  )
}

export default MobileHeader
